// src/Admin/EmailStudio/emailKit.js
/* =========================================================
   Email Studio — the email design and the ready templates.
   A new design, separate from the old Send Emails page.

   You write plain text. Rules in the text boxes:
   - an empty line starts a new paragraph
   - lines starting with "- " become a list
   - **text** becomes bold
   - links (https://…) become clickable
   - {{firstName}} {{studentName}} {{lastClassDate}} {{centerName}}
     are replaced for each person
   The email is built with tables + inline styles so it looks right in
   Gmail, Outlook and phone mail apps.
   ========================================================= */

export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://robomind-edu.com').replace(/\/$/, '');
const WA_NUMBER = String(process.env.REACT_APP_MOBILE_NUMBER1 || '').replace(/\D/g, '');
const INSTAGRAM_URL = 'https://www.instagram.com/robomind.lb';
const LOGO_URL = `${SITE_URL}/assets/img/logo/logowhitenew.png`;
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

export const esc = (t) =>
  String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export const waLink = (text = 'Hello! I have a question about RoboMind Academy') =>
  WA_NUMBER ? `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}` : SITE_URL;

/* ---------- color themes ---------- */
export const THEMES = {
  sunshine: { label: 'Sunshine', hero: '#FFD21F', heroText: '#0A1230', heroSub: '#3B3200', button: '#0A1230', buttonText: '#FFFFFF', accent: '#B98A00' },
  ocean: { label: 'Ocean', hero: '#1F6FD1', heroText: '#FFFFFF', heroSub: '#D6E7FF', button: '#FFD21F', buttonText: '#0A1230', accent: '#1F6FD1' },
  mint: { label: 'Mint', hero: '#10A36A', heroText: '#FFFFFF', heroSub: '#D2F5E6', button: '#0A1230', buttonText: '#FFFFFF', accent: '#0E8A5A' },
  grape: { label: 'Grape', hero: '#6C4CE0', heroText: '#FFFFFF', heroSub: '#E4DDFF', button: '#FFD21F', buttonText: '#0A1230', accent: '#6C4CE0' },
  night: { label: 'Night', hero: '#0A1230', heroText: '#FFFFFF', heroSub: '#B8C4E0', button: '#FFD21F', buttonText: '#0A1230', accent: '#1F6FD1' },
};

/* ---------- where the button goes ---------- */
export const LINKS = {
  whatsapp: { label: 'WhatsApp chat', url: () => waLink() },
  freetrial: { label: 'Free trial page', url: () => `${SITE_URL}/FreeTrial` },
  programs: { label: 'Programs page', url: () => `${SITE_URL}/Programs` },
  website: { label: 'Website', url: () => SITE_URL },
  custom: { label: 'Other link…', url: (custom) => custom || SITE_URL },
  none: { label: 'No button', url: () => '' },
};

/* ---------- text -> email HTML ---------- */
const inline = (t, color) =>
  esc(t)
    .replace(/\*\*(.+?)\*\*/g, `<strong style="color:${color};">$1</strong>`)
    .replace(/(https?:\/\/[^\s<]+[^\s<.,;:!?)])/g, '<a href="$1" style="color:#1F6FD1;text-decoration:underline;" target="_blank">$1</a>');

const paragraphs = (text, color = '#33405C', strong = '#0A1230') =>
  String(text || '')
    .replace(/\r/g, '')
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean)
    .map((block) => {
      const lines = block.split('\n');
      if (lines.every((l) => /^\s*[-•]\s+/.test(l))) {
        const items = lines
          .map((l) => `<li dir="auto" style="margin:0 0 6px;">${inline(l.replace(/^\s*[-•]\s+/, ''), strong)}</li>`)
          .join('');
        return `<ul style="margin:0 0 18px;padding-left:22px;color:${color};font-size:16px;line-height:1.6;">${items}</ul>`;
      }
      return `<p dir="auto" style="margin:0 0 18px;color:${color};font-size:16px;line-height:1.65;">${lines.map((l) => inline(l, strong)).join('<br>')}</p>`;
    })
    .join('\n');

/* up to 3 "highlight" lines shown as small cards with a check mark */
const highlightsHtml = (text, theme) => {
  const items = String(text || '')
    .split('\n')
    .map((l) => l.replace(/^\s*[-•]\s*/, '').trim())
    .filter(Boolean)
    .slice(0, 4);
  if (!items.length) return '';
  const rows = items
    .map(
      (it) => `
              <tr>
                <td width="34" valign="top" style="padding:10px 0 10px 14px;">
                  <div style="width:22px;height:22px;border-radius:11px;background:${theme.hero};color:${theme.heroText};font-size:13px;font-weight:800;line-height:22px;text-align:center;">&#10003;</div>
                </td>
                <td dir="auto" style="padding:10px 14px 10px 8px;font-size:15px;line-height:1.5;color:#0A1230;font-weight:600;">${inline(it, '#0A1230')}</td>
              </tr>`
    )
    .join('');
  return `
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 24px;background:#F6F8FC;border-radius:14px;">
              ${rows}
            </table>`;
};

/**
 * The full email. draft = { preheader, eyebrow, headline, intro, body, highlights,
 *   linkType, customUrl, buttonLabel, signature, theme, audience }
 */
export function buildEmail(draft) {
  const theme = THEMES[draft.theme] || THEMES.sunshine;
  const href = (LINKS[draft.linkType] || LINKS.none).url(draft.customUrl);
  const isCenter = draft.audience === 'centers';
  const why = isCenter
    ? 'You are receiving this email because we would love to work with your center.'
    : 'You are receiving this email because your family has been in touch with RoboMind Academy.';

  const button =
    href && draft.buttonLabel
      ? `
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:6px 0 28px;">
              <tr>
                <td align="center" bgcolor="${theme.button}" style="border-radius:999px;">
                  <a href="${esc(href)}" target="_blank" style="display:inline-block;padding:15px 34px;font-family:${FONT};font-size:16px;font-weight:800;color:${theme.buttonText};text-decoration:none;border-radius:999px;">${esc(draft.buttonLabel)} &rarr;</a>
                </td>
              </tr>
            </table>`
      : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="x-apple-disable-message-reformatting">
<title>${esc(draft.headline || 'RoboMind Academy')}</title>
<style>
  @media (max-width: 620px) {
    .es-card { width: 100% !important; border-radius: 0 !important; }
    .es-pad { padding-left: 22px !important; padding-right: 22px !important; }
    .es-h1 { font-size: 26px !important; }
  }
</style>
</head>
<body style="margin:0;padding:0;background:#EEF2F8;">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:#EEF2F8;">${esc(draft.preheader || '')}&#8203;&nbsp;&#8203;&nbsp;&#8203;&nbsp;&#8203;&nbsp;</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#EEF2F8" style="background:#EEF2F8;">
  <tr>
    <td align="center" style="padding:24px 12px;">
      <table role="presentation" class="es-card" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;background:#FFFFFF;border-radius:22px;overflow:hidden;">
        <tr>
          <td bgcolor="#0A1230" class="es-pad" style="background:#0A1230;padding:18px 36px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td align="left"><a href="${SITE_URL}" target="_blank" style="text-decoration:none;"><img src="${LOGO_URL}" alt="RoboMind Academy" height="34" style="height:34px;border:0;display:block;color:#FFFFFF;font-family:${FONT};font-size:18px;font-weight:800;"></a></td>
                <td align="right" style="font-family:${FONT};font-size:12px;color:#8FA0C8;letter-spacing:0.08em;text-transform:uppercase;">Coding · Robotics · AI</td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td bgcolor="${theme.hero}" class="es-pad" style="background:${theme.hero};padding:34px 36px 30px;font-family:${FONT};">
            ${draft.eyebrow ? `<p dir="auto" style="margin:0 0 10px;font-size:13px;font-weight:800;letter-spacing:0.12em;text-transform:uppercase;color:${theme.heroSub};">${esc(draft.eyebrow)}</p>` : ''}
            <h1 class="es-h1" dir="auto" style="margin:0;font-size:30px;line-height:1.18;font-weight:900;color:${theme.heroText};">${esc(draft.headline || '')}</h1>
            ${draft.intro ? `<p dir="auto" style="margin:14px 0 0;font-size:17px;line-height:1.55;color:${theme.heroSub};">${inline(draft.intro, theme.heroText)}</p>` : ''}
          </td>
        </tr>
        <tr>
          <td class="es-pad" style="padding:32px 36px 8px;font-family:${FONT};">
            ${paragraphs(draft.body)}
            ${highlightsHtml(draft.highlights, theme)}
            ${button}
          </td>
        </tr>
        <tr>
          <td class="es-pad" style="padding:0 36px 30px;font-family:${FONT};">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-top:1px solid #E6EAF2;">
              <tr>
                <td style="padding-top:20px;font-size:15px;line-height:1.5;color:#33405C;" dir="auto">
                  ${esc(draft.signOff || 'Warm regards,')}<br>
                  <strong style="color:#0A1230;">${esc(draft.signature || 'The RoboMind team')}</strong>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td bgcolor="#F6F8FC" style="background:#F6F8FC;padding:16px 24px;font-family:${FONT};" align="center">
            <a href="${waLink()}" target="_blank" style="color:#0A1230;text-decoration:none;font-weight:700;font-size:14px;">WhatsApp</a>
            <span style="color:#A0AAC0;">&nbsp;&nbsp;·&nbsp;&nbsp;</span>
            <a href="${INSTAGRAM_URL}" target="_blank" style="color:#0A1230;text-decoration:none;font-weight:700;font-size:14px;">Instagram</a>
            <span style="color:#A0AAC0;">&nbsp;&nbsp;·&nbsp;&nbsp;</span>
            <a href="${SITE_URL}" target="_blank" style="color:#0A1230;text-decoration:none;font-weight:700;font-size:14px;">Website</a>
          </td>
        </tr>
      </table>
      <table role="presentation" class="es-card" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;">
        <tr>
          <td align="center" style="padding:16px 20px 4px;font-family:${FONT};font-size:12px;line-height:1.6;color:#7A869E;">
            RoboMind Academy · Online 1-to-1 coding, robotics &amp; AI for kids · Lebanon<br>
            ${why}<br>
            Don’t want these emails? Reply with “unsubscribe” and we’ll remove you.
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
}

/* ---------- merge tags ---------- */
export const TAGS = {
  student: [
    ['{{firstName}}', 'Child first name'],
    ['{{studentName}}', 'Child full name'],
    ['{{lastClassDate}}', 'Last class date'],
    ['{{referralCode}}', 'Family invite code (enrolled students)'],
  ],
  center: [['{{centerName}}', 'Center name']],
};

export const firstNameOf = (full) => String(full || '').trim().split(/\s+/)[0] || '';

export function fillTags(text, values, { html = true } = {}) {
  const safe = (v) => (html ? esc(v) : String(v ?? ''));
  return String(text || '')
    .replace(/\{\{firstName\}\}/g, safe(values.firstName || 'your child'))
    .replace(/\{\{studentName\}\}/g, safe(values.studentName || 'your child'))
    .replace(/\{\{lastClassDate\}\}/g, safe(values.lastClassDate || 'their last class'))
    .replace(/\{\{centerName\}\}/g, safe(values.centerName || 'your center'))
    .replace(/\{\{referralCode\}\}/g, safe(values.referralCode || ''));
}

/* [placeholders] still left in the text */
export const leftovers = (...texts) => {
  const found = new Set();
  texts.forEach((t) => (String(t || '').match(/\[[^\]\n]{2,60}\]/g) || []).forEach((m) => found.add(m)));
  return [...found];
};

/* ---------- ready templates (new) ---------- */
const BLANK = {
  subject: '',
  preheader: '',
  eyebrow: '',
  headline: '',
  intro: '',
  body: '',
  highlights: '',
  linkType: 'whatsapp',
  customUrl: '',
  buttonLabel: 'Chat with us',
  signOff: 'Warm regards,',
  signature: 'The RoboMind team',
  theme: 'sunshine',
};

export const TEMPLATES = [
  {
    key: 'trial-next-step',
    name: 'Next step after the free class',
    fits: ['trial'],
    ...BLANK,
    subject: '{{firstName}}’s next step in coding 🚀',
    preheader: 'A place is waiting for {{firstName}} in a 1-to-1 class.',
    eyebrow: 'After your free class',
    headline: '{{firstName}} has already started. Let’s keep going!',
    intro: 'Thank you for trying RoboMind. We loved having {{firstName}} in class.',
    body: `In the free class, {{firstName}} took the first steps in building a real project. In our 1-to-1 online classes, the teacher follows {{firstName}}’s pace, so every session ends with something they made themselves.

Reply to this email or message us on WhatsApp, and we’ll suggest the right course and a weekly time that suits your family.`,
    highlights: `1-to-1 with the same teacher every week
A real project built in every session
A certificate at the end of each course`,
    linkType: 'whatsapp',
    buttonLabel: 'Book {{firstName}}’s classes',
    theme: 'sunshine',
  },
  {
    key: 'trial-missed',
    name: 'Missed the free class',
    fits: ['trial'],
    ...BLANK,
    subject: 'Let’s find a new time for {{firstName}}',
    preheader: 'The free class is still waiting for {{firstName}}.',
    eyebrow: 'Your free class',
    headline: 'We missed {{firstName}}!',
    intro: 'No problem at all, plans change. The free class is still yours.',
    body: `Pick a new time that suits you from our free trial page, or simply reply to this email and we’ll arrange it together.

All {{firstName}} needs is a laptop or a computer, and a little curiosity.`,
    highlights: '',
    linkType: 'freetrial',
    buttonLabel: 'Pick a new time',
    theme: 'ocean',
  },
  {
    key: 'come-back',
    name: 'We’d love to have you back',
    fits: ['former'],
    ...BLANK,
    subject: 'A new project is waiting for {{firstName}}',
    preheader: 'Pick up where {{firstName}} left off.',
    eyebrow: 'It’s been a while',
    headline: 'Ready for the next level, {{firstName}}?',
    intro: '{{firstName}}’s last class with us was on {{lastClassDate}}. We’d love to continue the journey.',
    body: `Since then, we’ve added new courses in game development, AI and robotics, made for exactly where {{firstName}} stopped.

Reply to this email or message us on WhatsApp, and we’ll suggest the best next course.`,
    highlights: `Continue with the same teacher if you like
A new course matched to {{firstName}}’s level`,
    linkType: 'whatsapp',
    buttonLabel: 'Restart classes',
    theme: 'grape',
  },
  {
    key: 'new-course',
    name: 'New course announcement',
    fits: ['trial', 'current', 'former', 'saved'],
    ...BLANK,
    subject: 'New at RoboMind: [course name]',
    preheader: 'A brand-new course for kids aged [ages].',
    eyebrow: 'New course',
    headline: '[Course name] is here!',
    intro: 'A new course where kids [what they will build].',
    body: `[Two or three sentences about the course and why kids will love it.]

Places are limited, so reply to this email or message us on WhatsApp to save a spot for {{firstName}}.`,
    highlights: `For ages [ages]
[number] sessions, 1-to-1 online
Final project: [project]`,
    linkType: 'programs',
    buttonLabel: 'See the course',
    theme: 'mint',
  },
  {
    key: 'offer',
    name: 'Limited-time offer',
    fits: ['trial', 'current', 'former', 'allpaid', 'saved'],
    ...BLANK,
    subject: 'A special offer for {{firstName}} ⏳',
    preheader: 'Valid until [date].',
    eyebrow: 'Until [date]',
    headline: '[Offer, e.g. 2 free sessions] when you book this week',
    intro: 'A small thank-you for being part of the RoboMind family.',
    body: `Book a package before **[date]** and get [offer details].

Message us on WhatsApp to claim it.`,
    highlights: '',
    linkType: 'whatsapp',
    buttonLabel: 'Claim the offer',
    theme: 'night',
  },
  {
    key: 'center',
    name: 'Partnership with a center',
    fits: ['centers'],
    ...BLANK,
    subject: 'Coding & robotics classes for {{centerName}}',
    preheader: 'A ready program for your students, online or on site.',
    eyebrow: 'Partnership',
    headline: 'Bring coding and AI to {{centerName}}',
    intro: 'RoboMind runs ready-made coding, robotics and AI programs for schools and centers.',
    body: `We provide the curriculum, the teachers and the projects. Your students build games, apps and robots, and finish with a certificate.

Could we book a short call to see what fits {{centerName}}?`,
    highlights: `Workshops or full courses
Online or at your center
Certificates for every student`,
    linkType: 'whatsapp',
    buttonLabel: 'Book a call',
    theme: 'ocean',
  },
  {
    key: 'referral',
    name: 'Invite a friend (referral)',
    fits: ['current', 'allpaid', 'saved'],
    ...BLANK,
    subject: 'Invite a friend to RoboMind: you both get 10% off 🎁',
    preheader: 'Your family’s personal invite link is inside.',
    eyebrow: 'New: invite a friend',
    headline: 'Share RoboMind, save on your next package',
    intro: 'Thank you for trusting us with {{firstName}}’s learning. Many of our families come from friends like you, so we made it worth it for both of you.',
    body: `Share your family’s personal link with friends who have children aged 6 to 17:

${SITE_URL}/FreeTrial?ref={{referralCode}}

Their child gets a **free class**, and if they join, they get **10% off their first package**.

You get **10% off your next package for every friend who joins**, up to 30%. Two friends join = 20% off, three friends = 30% off. You can also find your link anytime on {{firstName}}’s dashboard.`,
    highlights: `Your friend: free class + 10% off
You: 10% off per friend who joins
Up to 30% off your next package`,
    linkType: 'custom',
    customUrl: `https://wa.me/?text=${encodeURIComponent('Hi! My child is learning coding with RoboMind and loves it 🤖\nBook a free class for your child here, and get 10% off the first package if you join:\n' + SITE_URL + '/FreeTrial?ref=')}{{referralCode}}`,
    buttonLabel: 'Share on WhatsApp',
    theme: 'sunshine',
  },
  {
    key: 'competition',
    name: 'New competition',
    fits: ['allpaid', 'current', 'former', 'saved'],
    ...BLANK,
    subject: 'New RoboMind challenge: [challenge name] 🏆',
    preheader: 'Open to all our students until [last day].',
    eyebrow: 'Monthly challenge',
    headline: '[Challenge name]',
    intro: 'A new challenge is open for all RoboMind students, and {{firstName}} is invited!',
    body: `[What to build]

Send the project link from the **Competitions** page on {{firstName}}’s dashboard before **[last day]**. Our teachers score every project, and the winners are announced to all students.`,
    highlights: `Open to: [who]
Last day: [last day]
Prize: [prize]`,
    linkType: 'custom',
    customUrl: `${SITE_URL}/student/competitions`,
    buttonLabel: 'Join the challenge',
    theme: 'sunshine',
  },
  { key: 'blank', name: 'Start from blank', fits: ['trial', 'current', 'former', 'allpaid', 'centers', 'saved'], ...BLANK },
];
